import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Workflow } from 'lucide-react';

import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterFlash }        from '@/components/master/MasterFlash';
import { MasterAuditTab }     from '@/components/master/MasterAuditTab';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Badge }               from '@/components/ui/Badge';
import { Tabs } from '@/components/ui/Tabs';
import { Toggle } from '@/components/ui/Toggle';
import { Combobox } from '@/components/ui/Combobox';
import { Card }                from '@/components/ui/Card';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }    from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { Permissions }         from '@/constants/permissions';
import { AppRoutes }           from '@/constants/routes';
import { transactionTypeApi }  from '@/api/transactionType.api';
import type { TransactionTypeDto, TransactionTypeListDto } from '@/types/transactionType.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  typeCode:             z.string().min(1, 'Type code is required').max(20),
  description:          z.string().min(1, 'Description is required').max(200),
  sourceModule:         z.enum(['GL', 'AR', 'AP', 'BANK', 'FA', 'INV', 'PAY']),
  isActive:             z.boolean(),
  numberingSeriesCode:  z.string().max(50).nullable().optional(),
  workflowMapping:      z.string().max(100).nullable().optional(),
  postingRuleMapping:   z.string().max(100).nullable().optional(),
  approvalRequired:     z.boolean(),
  autoPosting:          z.boolean(),
  reversalAllowed:      z.boolean(),
  editAfterApproval:    z.boolean(),
  printFormatCode:      z.string().max(50).nullable().optional(),
  reportCode:           z.string().max(50).nullable().optional(),
  permissionCode:       z.string().max(100).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  typeCode: '', description: '', sourceModule: 'GL', isActive: true,
  numberingSeriesCode: null, workflowMapping: null, postingRuleMapping: null,
  approvalRequired: false, autoPosting: false, reversalAllowed: true, editAfterApproval: false,
  printFormatCode: null, reportCode: null, permissionCode: null,
};

const TABS = ['General', 'Workflow & Posting', 'Integration', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<TransactionTypeListDto>[] = [
  { key: 'typeCode',     label: 'Code',   className: 'w-24 font-mono' },
  { key: 'description',  label: 'Description' },
  { key: 'sourceModule', label: 'Module', className: 'w-20' },
  { key: 'approvalRequired', label: 'Approval', className: 'w-20',
    render: r => r.approvalRequired ? <Badge variant="default">Required</Badge> : null },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: TransactionTypeDto): FormValues {
  return {
    typeCode: dto.typeCode, description: dto.description,
    sourceModule: dto.sourceModule as 'GL' | 'AR' | 'AP' | 'BANK' | 'FA' | 'INV' | 'PAY',
    isActive: dto.isActive,
    numberingSeriesCode: dto.numberingSeriesCode ?? null,
    workflowMapping: dto.workflowMapping ?? null, postingRuleMapping: dto.postingRuleMapping ?? null,
    approvalRequired: dto.approvalRequired, autoPosting: dto.autoPosting,
    reversalAllowed: dto.reversalAllowed, editAfterApproval: dto.editAfterApproval,
    printFormatCode: dto.printFormatCode ?? null, reportCode: dto.reportCode ?? null,
    permissionCode: dto.permissionCode ?? null,
  };
}

/** Binds a boolean to the shared Toggle. Kept local only to name the props this page uses. */
const ToggleSwitch = ({
  label, description, checked, onChange, disabled,
}: {
  label: string; description?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) => (
  <Toggle label={label} description={description}
    checked={checked} onChange={onChange} disabled={disabled} />
);

// ── Component ─────────────────────────────────────────────────────────────────

export const TransactionTypePage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.CFG_TRANSACTION_TYPE_VIEW);
  const canCreate = hasPermission(Permissions.CFG_TRANSACTION_TYPE_CREATE);
  const canEdit   = hasPermission(Permissions.CFG_TRANSACTION_TYPE_EDIT);
  const canDelete = hasPermission(Permissions.CFG_TRANSACTION_TYPE_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [record,       setRecord]       = useState<TransactionTypeDto | null>(null);
  const [codeInput,    setCodeInput]    = useState('');
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);
  const [printOpen,    setPrintOpen]    = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const isActive = watch('isActive');

  const resetToInitial = useCallback(() => {
    setRecord(null); setCodeInput(''); reset(EMPTY_FORM);
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
    setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: TransactionTypeDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setCodeInput(dto.typeCode);
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view transaction types.'); return; }
    setLookupBusy(true);
    try {
      const res = await transactionTypeApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Transaction type '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset({ ...EMPTY_FORM, typeCode: code }); setCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleRefresh   = () => resetToInitial();
  const handleEdit      = () => screen.setMode('edit');
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await transactionTypeApi.create({
          typeCode: d.typeCode.trim().toUpperCase(), description: d.description.trim(),
          sourceModule: d.sourceModule,
          numberingSeriesCode: n(d.numberingSeriesCode), workflowMapping: n(d.workflowMapping),
          postingRuleMapping: n(d.postingRuleMapping), approvalRequired: d.approvalRequired,
          autoPosting: d.autoPosting, reversalAllowed: d.reversalAllowed,
          editAfterApproval: d.editAfterApproval, printFormatCode: n(d.printFormatCode),
          reportCode: n(d.reportCode), permissionCode: n(d.permissionCode),
        });
        flash('success', 'Transaction type created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await transactionTypeApi.update(record.id, {
          description: d.description.trim(), sourceModule: d.sourceModule,
          numberingSeriesCode: n(d.numberingSeriesCode), workflowMapping: n(d.workflowMapping),
          postingRuleMapping: n(d.postingRuleMapping), approvalRequired: d.approvalRequired,
          autoPosting: d.autoPosting, reversalAllowed: d.reversalAllowed,
          editAfterApproval: d.editAfterApproval, printFormatCode: n(d.printFormatCode),
          reportCode: n(d.reportCode), permissionCode: n(d.permissionCode), isActive: d.isActive,
        });
        flash('success', 'Transaction type updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await transactionTypeApi.remove(record.id);
      flash('success', `Transaction type '${record.typeCode}' deleted.`); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handlePrint = () => { if (record) setPrintOpen(true); };
  const handleExit  = () => navigate(AppRoutes.DASHBOARD);

  const fetchTypes = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await transactionTypeApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const handleSearchSelect = (item: TransactionTypeListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    transactionTypeApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load transaction type.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={Workflow} title="Transaction Type Setup"
        subtitle="Configure ERP transaction behavior and routing" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit} onPrint={handlePrint}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Type Code"
        summary={record && (<>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.description}</span>
            <Badge variant="default">{record.sourceModule}</Badge>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter a transaction type code and press Enter, or browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search transaction types"
          placeholder="Type code + Enter…"
          maxLength={20}
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Transaction Type Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Type Code" {...register('typeCode')} disabled
                    placeholder="— enter via code field above —" className="font-mono uppercase" />
                  <div>
                    <Controller name="sourceModule" control={control} render={({ field }) => (
                      <Combobox label="Source Module *" clearable={false}
                        options={['GL', 'AR', 'AP', 'BANK', 'FA', 'INV', 'PAY'].map(o => ({ value: o, label: o }))}
                        value={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />
                    {errors.sourceModule && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.sourceModule.message}</p>}
                  </div>
                  <Input label="Description *" {...register('description')} error={errors.description?.message}
                    disabled={fieldsDisabled} placeholder="Transaction type description" className="sm:col-span-2" />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <Controller name="isActive" control={control} render={({ field }) => (
                        <ToggleSwitch label={isActive ? 'Active' : 'Inactive'} checked={field.value}
                          onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Workflow & Posting' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Workflow & Posting Configuration</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {[
                    { name: 'numberingSeriesCode' as const, label: 'Numbering Series Code', ph: 'e.g. GL-JV-YYYY' },
                    { name: 'workflowMapping'     as const, label: 'Workflow Mapping',       ph: 'e.g. GL_APPROVAL_WF' },
                    { name: 'postingRuleMapping'  as const, label: 'Posting Rule Mapping',   ph: 'e.g. GL_STD_RULE' },
                  ].map(({ name, label, ph }) => (
                    <div key={name}>
                      <Input label={label} {...register(name)} disabled={fieldsDisabled} placeholder={ph} className="font-mono" />
                    </div>
                  ))}
                </div>
                <div className="space-y-0">
                  {([
                    // What actually routes a document for approval is a workflow, resolved on
                    // submit from Setup -> Workflow Setup. This flag records the intention and is
                    // read by no posting path, so the old description — "must be approved before
                    // posting" — promised an enforcement that does not come from here.
                    ['approvalRequired',  'Approval Required',       'Marks this type as needing sign-off. Set up who approves it, and in what order, under Workflow Setup — that is what routes the document.'],
                    ['autoPosting',       'Auto Posting',            'Transactions are automatically posted without manual review.'],
                    ['reversalAllowed',   'Reversal Allowed',        'Allows creation of reversal transactions.'],
                    ['editAfterApproval', 'Edit After Approval',     'Allows editing transactions after they have been approved.'],
                  ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                    <Controller key={fieldName} name={fieldName} control={control} render={({ field }) => (
                      <ToggleSwitch label={label} description={desc}
                        checked={field.value as boolean} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />
                  ))}
                </div>
              </Card>
            )}

            {activeTab === 'Integration' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Integration Configuration</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: 'printFormatCode' as const, label: 'Print Format Code',  ph: 'e.g. RPT_GL_JV_STANDARD' },
                    { name: 'reportCode'      as const, label: 'Report Code',         ph: 'e.g. GL_JV_DETAIL' },
                    { name: 'permissionCode'  as const, label: 'Permission Code',     ph: 'e.g. CREATE_GL_JOURNAL' },
                  ].map(({ name, label, ph }) => (
                    <div key={name}>
                      <Input label={label} {...register(name)} disabled={fieldsDisabled} placeholder={ph} className="font-mono" />
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      <MasterSearchModal<TransactionTypeListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Transaction Types" columns={SEARCH_COLUMNS}
        fetchData={fetchTypes} onSelect={handleSearchSelect}
      />

      <ConfirmDialog isOpen={showNewConfirm !== null} variant="info"
        title="Create New Transaction Type"
        message={<span>Type <strong className="font-mono">{showNewConfirm}</strong> does not exist. Create a new record?</span>}
        confirmLabel="Yes, Create" cancelLabel="No"
        onConfirm={handleConfirmCreateNew} onCancel={() => setShowNewConfirm(null)}
      />
      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Transaction Type' : 'Save Changes'}
        message={screen.isNew ? 'Save this new transaction type?' : `Save changes to "${record?.typeCode}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />
      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Transaction Type"
        message={<span>Delete transaction type <strong>{record?.typeCode} — {record?.description}</strong>?</span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Transaction Type Report">
          <ReportHeader reportTitle="Transaction Type Configuration Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[{ label: 'Type Code', value: record.typeCode }]}
          />
          <ReportSection title="General" fields={[
            { label: 'Type Code',    value: record.typeCode },
            { label: 'Description', value: record.description, wide: true },
            { label: 'Module',      value: record.sourceModule },
            { label: 'Status',      value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Workflow & Posting" fields={[
            { label: 'Numbering Series', value: record.numberingSeriesCode },
            { label: 'Workflow',         value: record.workflowMapping },
            { label: 'Posting Rules',    value: record.postingRuleMapping },
            { label: 'Approval Required', value: record.approvalRequired, display: record.approvalRequired ? 'Yes' : 'No' },
            { label: 'Auto Posting',     value: record.autoPosting, display: record.autoPosting ? 'Yes' : 'No' },
            { label: 'Reversal Allowed', value: record.reversalAllowed, display: record.reversalAllowed ? 'Yes' : 'No' },
            { label: 'Edit After Approval', value: record.editAfterApproval, display: record.editAfterApproval ? 'Yes' : 'No' },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
